import { FilledInputProps } from '@mui/material/FilledInput';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import React, { ReactElement } from 'react';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import { useFormProviderRCPropsContext } from '@/components/form/FormProviderRC.comp.tsx';
import { handleKeyDownDefault } from '@/components/form/util/handleKeyDownDefault.util.ts';
import { isFormFieldRequired } from '@/utils/form/isFormFieldRequired.util.ts';

import { FormPropsModel } from './model/FormProps.model';
import { TextError } from './TextError.comp';

export interface TextFormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends FormPropsModel<TName> {
  EndIcon?: React.ReactNode;
  maxLength?: number;
  maxRows?: number;
  minRows?: number;
  multiline?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onChangeNotStopping?: (event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  StartIcon?: React.ReactNode;
  type?: React.InputHTMLAttributes<unknown>['type'];
  value?: string;
}

export const TextFormField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: TextFormFieldProps<TFieldValues, TName>,
): ReactElement<TextFormFieldProps<TFieldValues, TName>> => {
  const { control } = useFormContext<TFieldValues>();
  const {
    EndIcon,
    fullWidth,
    id,
    label,
    maxLength,
    maxRows,
    minRows,
    multiline,
    name,
    onChange,
    onChangeNotStopping,
    placeholder,
    StartIcon,
    ...rest
  } = props;
  const { validation } = useFormProviderRCPropsContext();
  const render: ControllerProps<TFieldValues, TName>['render'] = ({
    field,
    fieldState: { error },
  }) => {
    const isRequired = isFormFieldRequired(field.name, validation);

    const handleChange: FilledInputProps['onChange'] = event => {
      if (onChange) {
        onChange(event);
      } else {
        onChangeNotStopping?.(event);
        field.onChange(event);
      }
    };

    return (
      <FormControl fullWidth={fullWidth ?? true}>
        <TextField
          key={name}
          {...field}
          {...rest}
          {...(label && {
            label,
            sx: {
              ...rest.sx,
              fieldset: {
                background: 'none',
              },
            },
          })}
          error={Boolean(error)}
          hiddenLabel={!label}
          id={id && `input-${id}`}
          maxRows={maxRows ?? (minRows ?? 0) + 2}
          minRows={minRows}
          multiline={multiline}
          onChange={handleChange}
          onKeyDown={handleKeyDownDefault({ ignore: multiline })}
          placeholder={placeholder ?? label}
          size={'small'}
          slotProps={{
            input: {
              endAdornment: EndIcon && <InputAdornment position="end">{EndIcon}</InputAdornment>,
              startAdornment: StartIcon && (
                <InputAdornment position="start">{StartIcon}</InputAdornment>
              ),
            },
            ...(label && {
              inputLabel: {
                disableAnimation: false,
                required: isRequired,
                shrink: true,
              },
            }),
          }}
          variant="standard"
        />
        <TextError error={error} />
      </FormControl>
    );
  };

  return (
    <Controller<TFieldValues, TName>
      control={control}
      name={name}
      render={render}
      rules={{
        maxLength,
      }}
    />
  );
};
